import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { OverlayDrawer, scrim } from '@theme/overlay-drawer';
import { throttle } from '@theme/utilities';

/**
 * @typedef {Object} SiteHeaderRefs
 * @property {HTMLElement} header The `<header class="site-header">` element
 * @property {HTMLElement[]} [menuItems] Nav items with a mega menu (`.site-nav__item`)
 * @property {HTMLButtonElement[]} [menuTriggers] Their `.site-nav__trigger` buttons
 */

/** Scrim owner name for the mega menu */
const SCRIM_OWNER = 'mega-menu';
/** A click this soon after hover intent opened the panel does not close it */
const CLICK_AFTER_HOVER_MS = 600;

/**
 * Site header: scrolled state, hide on scroll down, and the desktop mega menu
 * (hover intent with the mouse, click and keyboard everywhere).
 *
 * Markup: wraps `<header ref="header">`; mega menu items carry `ref="menuItems[]"`
 * with `on:pointerenter="/onItemEnter" on:pointerleave="/onItemLeave" on:focusout="/onItemFocusout"`,
 * their buttons `ref="menuTriggers[]" on:click="/onTriggerClick"`. The shared scrim inside closes the
 * menu with `on:pointerenter="/closeMenu"`.
 *
 * @extends {Component<SiteHeaderRefs>}
 */
class SiteHeader extends Component {
  requiredRefs = ['header'];

  /** @type {HTMLElement | null} */
  #openItem = null;
  #openedAt = 0;
  #lastY = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  connectedCallback() {
    super.connectedCallback();
    // Header and bar heights are design tokens in CSS; only flag a missing bar
    if (!document.querySelector('.announcement-bar')) {
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
    }

    this.#lastY = window.scrollY;
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('keydown', this.#onKeydown, true);
    document.addEventListener(ThemeEvents.overlayOpen, this.#onOverlayOpen);
    this.#updateScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('keydown', this.#onKeydown, true);
    document.removeEventListener(ThemeEvents.overlayOpen, this.#onOverlayOpen);
    clearTimeout(this.#closeTimer);
    this.closeMenu();
  }

  /** @param {PointerEvent} event */
  onItemEnter(event) {
    const item = this.#itemFrom(event);
    if (!item || event.pointerType !== 'mouse') return;
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#openTimer);
    this.#openTimer = setTimeout(() => this.#openMenu(item), this.#openItem ? 0 : 90);
  }

  /** @param {PointerEvent} event */
  onItemLeave(event) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.closeMenu(), 180);
  }

  /** @param {MouseEvent} event */
  onTriggerClick(event) {
    const item = this.#itemFrom(event);
    if (!item) return;
    if (this.#openItem === item) {
      if (Date.now() - this.#openedAt > CLICK_AFTER_HOVER_MS) this.closeMenu();
    } else {
      this.#openMenu(item);
    }
  }

  /** @param {FocusEvent} event */
  onItemFocusout(event) {
    const item = this.#itemFrom(event);
    const next = event.relatedTarget;
    if (!item || this.#openItem !== item) return;
    if (next instanceof Node && item.contains(next)) return;
    this.closeMenu();
  }

  /** Closes the open mega menu panel, if any. */
  closeMenu() {
    clearTimeout(this.#openTimer);
    const item = this.#openItem;
    if (!item) return;
    item.classList.remove('is-open');
    this.#triggerOf(item)?.setAttribute('aria-expanded', 'false');
    this.#openItem = null;
    this.refs.header?.classList.remove('is-open');
    scrim.hide(SCRIM_OWNER);
  }

  /** @param {HTMLElement} item */
  #openMenu(item) {
    if (this.#openItem === item) return;
    if (this.#openItem) {
      this.#openItem.classList.remove('is-open');
      this.#triggerOf(this.#openItem)?.setAttribute('aria-expanded', 'false');
    }
    this.#openItem = item;
    this.#openedAt = Date.now();
    item.classList.add('is-open');
    this.#triggerOf(item)?.setAttribute('aria-expanded', 'true');
    this.refs.header.classList.add('is-open');
    scrim.show(SCRIM_OWNER);
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #itemFrom(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    const item = target.closest('.site-nav__item');
    return item instanceof HTMLElement && (this.refs.menuItems || []).includes(item) ? item : null;
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLButtonElement | undefined}
   */
  #triggerOf(item) {
    return (this.refs.menuTriggers || []).find((trigger) => item.contains(trigger));
  }

  #updateScroll() {
    const header = this.refs.header;
    if (!header) return;
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > 8);
    const goingDown = y > this.#lastY + 4;
    const goingUp = y < this.#lastY - 4;
    if (goingDown && y > 240 && !OverlayDrawer.active) header.classList.add('is-hidden');
    else if (goingUp || y < 120) header.classList.remove('is-hidden');
    if (goingDown || goingUp) this.#lastY = y;
  }

  #onScroll = throttle(() => this.#updateScroll());

  /**
   * Runs in the capture phase, before overlays: Escape closes the mega menu first.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#openItem) return;
    event.preventDefault();
    const trigger = this.#triggerOf(this.#openItem);
    this.closeMenu();
    trigger?.focus();
  };

  #onOverlayOpen = () => {
    this.closeMenu();
  };
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}
